import { RsObjectLiteral } from '@rs-shared-lib/models';
import {
  RS_DEFAULT_DATE_FORMAT_FOR_REQUESTS,
  RsDateFormatContext,
  RsDateFormatMapContext
} from '@app/shared/interceptors/request-params/request-date-formatter.tokens';

/* ------------------------------------------------------------------ */
/* Date formatting (UTC, explicit tokens only)                        */
/* ------------------------------------------------------------------ */

/**
 * Formats a Date object to a UTC string using the specified format pattern.
 *
 * **Always uses UTC** to ensure consistent formatting regardless of server/client timezone.
 *
 * **Emits console warning for:** Invalid Date objects (e.g., `new Date('invalid')`)
 *
 * @param date - The Date object to format
 * @param format - Format string using supported tokens
 * @returns Formatted date string in UTC (or string with 'NaN' if date is invalid)
 *
 * @example
 * ```typescript
 * formatDateUTC(new Date('2024-03-15T10:30:00Z'), 'YYYY-MM-DD')
 * // => '2024-03-15'
 *
 * formatDateUTC(new Date('invalid'), 'YYYY-MM-DD')
 * // => 'NaN-NaN-NaN' (⚠️ warns: invalid Date object)
 * ```
 *
 * **Supported tokens:**
 * - `YYYY` - 4-digit year (e.g., 2024)
 * - `MM` - 2-digit month with leading zero (01-12)
 * - `DD` - 2-digit day with leading zero (01-31)
 * - `HH` - 2-digit hour in 24h format with leading zero (00-23)
 * - `mm` - 2-digit minute with leading zero (00-59)
 * - `ss` - 2-digit second with leading zero (00-59)
 */
function _formatDateUTC(date: Date, format: string): string {
  // Warn if Date is invalid
  if (Number.isNaN(date.getTime())) {
    console.warn(
      `[rsRecursiveDateFormatter] Attempting to format an invalid Date object. ` +
      `Result will contain NaN values.`,
      date
    );
  }

  const parts: Record<string, string> = {
    YYYY: String(date.getUTCFullYear()),
    MM: String(date.getUTCMonth() + 1).padStart(2, '0'),
    DD: String(date.getUTCDate()).padStart(2, '0'),
    HH: String(date.getUTCHours()).padStart(2, '0'),
    mm: String(date.getUTCMinutes()).padStart(2, '0'),
    ss: String(date.getUTCSeconds()).padStart(2, '0'),
  };

  return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (token) => parts[token] || token);
}

/**
 * Formats a Date object to a string using the specified format pattern.
 *
 * **Uses LOCAL timezone** to preserve the date as displayed to the user.
 * This is appropriate for date pickers where users select calendar dates.
 *
 * **Emits console warning for:** Invalid Date objects (e.g., `new Date('invalid')`)
 *
 * @param date - The Date object to format
 * @param format - Format string using supported tokens
 * @returns Formatted date string in local time (or string with 'NaN' if date is invalid)
 *
 * @example
 * ```typescript
 * // User in GMT-3 selects Jan 31, 2026
 * formatDateLocal(new Date(2026, 0, 31), 'YYYY-MM-DD')
 * // => '2026-01-31' ✅ (preserves user's selection)
 * ```
 *
 * **Supported tokens:**
 * - `YYYY` - 4-digit year (e.g., 2024)
 * - `MM` - 2-digit month with leading zero (01-12)
 * - `DD` - 2-digit day with leading zero (01-31)
 * - `HH` - 2-digit hour in 24h format with leading zero (00-23)
 * - `mm` - 2-digit minute with leading zero (00-59)
 * - `ss` - 2-digit second with leading zero (00-59)
 */
function formatDateLocal(date: Date, format: string): string {
  // Warn if Date is invalid
  if (Number.isNaN(date.getTime())) {
    console.warn(
      `[rsRecursiveDateFormatter] Attempting to format an invalid Date object. ` +
      `Result will contain NaN values.`,
      date
    );
  }

  const parts: Record<string, string> = {
    YYYY: String(date.getFullYear()),
    MM: String(date.getMonth() + 1).padStart(2, '0'),
    DD: String(date.getDate()).padStart(2, '0'),
    HH: String(date.getHours()).padStart(2, '0'),
    mm: String(date.getMinutes()).padStart(2, '0'),
    ss: String(date.getSeconds()).padStart(2, '0'),
  };

  return format.replace(/YYYY|MM|DD|HH|mm|ss/g, (token) => parts[token] || token);
}

/* ------------------------------------------------------------------ */
/* Strict ISO-8601 detection                                          */
/* ------------------------------------------------------------------ */
/**
 * Checks if a value is a strictly valid ISO-8601 date or date-time string.
 *
 * Performs three-stage validation:
 * 1. **Regex validation** - Ensures format matches ISO-8601 pattern with proper ranges
 * 2. **Date parsing** - Ensures JavaScript can parse it as a date
 * 3. **Round-trip validation** - Ensures the parsed date matches the original string (rejects '2024-02-31')
 *
 * **Emits console warnings for:**
 * - ISO-like strings that are calendar-invalid (e.g., '2024-02-31', '2024-04-31')
 * - Strings matching ISO format but with invalid time components
 *
 * @param value - Value to check
 * @returns `true` if value is a valid ISO-8601 string, `false` otherwise
 *
 * @example
 * ```typescript
 * isStrictISODateString('2024-03-15T10:30:00Z')  // => true
 * isStrictISODateString('2024-03-15')            // => true
 * isStrictISODateString('March 15, 2024')        // => false (no warning)
 * isStrictISODateString('2024-3-5')              // => false (missing leading zeros, no warning)
 * isStrictISODateString('2024-02-31')            // => false (⚠️ warns: calendar invalid)
 * isStrictISODateString('2024-13-01')            // => false (invalid month, caught by regex)
 * isStrictISODateString('2024-04-31')            // => false (⚠️ warns: April has only 30 days)
 * isStrictISODateString('2024-03-15T25:00:00Z')  // => false (⚠️ warns: invalid hour)
 * ```
 *
 * todo: Replace with Temporal when available
 * try {
 *   const date = Temporal.PlainDate.from('2024-02-31'); // throws RangeError
 * } catch (e) {
 *   console.log('Invalid date'); // ✅ catches invalid calendar date
 * }
 */
export function isStrictISODateString(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  /**
   * Strict ISO-8601 date/date-time string regex validator.
   *
   * **Accepts:**
   * - Date only: `YYYY-MM-DD` (e.g., '2024-03-15')
   * - Date-time with Z: `YYYY-MM-DDTHH:mm:ss.SSSZ` (e.g., '2024-03-15T10:30:00.123Z')
   * - Date-time with offset: `YYYY-MM-DDTHH:mm:ss±HH:mm` (e.g., '2024-03-15T10:30:00+02:00')
   *
   * **Rejects:**
   * - Missing leading zeros (e.g., '2024-3-5')
   * - Invalid months/days (e.g., '2024-13-01', '2024-02-31')
   * - Non-ISO formats (e.g., '15/03/2024', 'March 15, 2024')
   * - Partial dates (e.g., '2024', '2024-03')
   */
  const ISO_8601_REGEX =
    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?:T([01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{3})?(?:Z|[+-][01]\d:[0-5]\d)?)?$/;

  if (!ISO_8601_REGEX.test(value)) {
    return false;
  }

  // Try parsing to Date
  let date: Date;
  try {
    date = new Date(value);
  } catch {
    return false;
  }

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  // Strict round-trip: parse then convert to ISO string
  // Compare only if original string includes time component
  if (value.includes('T')) {
    // Normalize original string to canonical ISO format for comparison
    const normalizedOriginal = new Date(value).toISOString();
    if (normalizedOriginal !== date.toISOString()) {
      console.warn(`[rsRecursiveDateFormatter] ISO string round-trip mismatch: "${value}" -> "${date.toISOString()}"`);
      return false;
    }
  } else {
    // Date-only string: compare year-month-day
    const ymd = (date: Date) =>
      `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
    if (ymd(date) !== value) {
      console.warn(`[rsRecursiveDateFormatter] Calendar-invalid date string detected: "${value}" -> "${ymd(date)}"`);
      return false;
    }
  }

  return true;
}

/* ------------------------------------------------------------------ */
/* Type guards                                                        */
/* ------------------------------------------------------------------ */

/**
 * Type guard to check if a value is a plain JavaScript object.
 *
 * Excludes: `null`, arrays, Date objects, and other special objects.
 *
 * @param value - Value to check
 * @returns `true` if value is a plain object
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

/* ------------------------------------------------------------------ */
/* Deep clone (safe, predictable)                                     */
/* ------------------------------------------------------------------ */

/**
 * Creates a deep clone of any value with circular reference protection.
 *
 * **Features:**
 * - Handles primitives, objects, arrays, and Date objects
 * - Protects against circular references using WeakMap
 * - Preserves Date objects as new Date instances
 * - Immutable - does not modify the original
 *
 * @param value - Value to clone
 * @param visited - Internal WeakMap for tracking circular references
 * @returns Deep clone of the input value
 *
 * @example
 * ```typescript
 * const obj = { date: new Date(), nested: { value: 42 } };
 * const clone = deepClone(obj);
 *
 * obj.nested.value = 100;
 * console.log(clone.nested.value); // => 42 (unchanged)
 * ```
 */
function deepClone<T>(value: T, visited = new WeakMap<object, unknown>()): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (visited.has(value as object)) {
    return visited.get(value as object) as T;
  }

  if (value instanceof Date) {
    return new Date(value.getTime()) as T;
  }

  if (Array.isArray(value)) {
    const clone: unknown[] = [];
    visited.set(value, clone);
    for (const item of value) {
      clone.push(deepClone(item, visited));
    }
    return clone as T;
  }

  const clone: Record<string, unknown> = {};
  visited.set(value as object, clone);

  for (const key of Object.keys(value)) {
    clone[key] = deepClone((value as Record<string, unknown>)[key], visited);
  }

  return clone as T;
}

/* ------------------------------------------------------------------ */
/* Main formatter                                                     */
/* ------------------------------------------------------------------ */

/**
 * Recursively formats all Date objects and ISO-8601 date strings in an object tree.
 *
 * **Key behaviors:**
 * - Converts Date objects to formatted UTC strings
 * - Converts valid ISO-8601 strings to formatted UTC strings
 * - Leaves non-ISO strings unchanged (e.g., 'March 15, 2024' passes through)
 * - Recursively processes nested objects and arrays
 * - Supports per-property format overrides via `formatMap`
 * - Immutable - does not modify the original object
 *
 * @template T - Type of the input object (extends RsObjectLiteral)
 * @param objectToFormat - Object containing dates to format
 * @param format - Default format pattern (default: 'YYYY-MM-DD')
 * @param formatMap - Optional map of property paths to specific formats
 * @returns New object with all dates formatted as strings
 *
 * @example
 * **Basic usage:**
 * ```typescript
 * const input = {
 *   createdAt: new Date('2024-03-15T10:30:00Z'),
 *   description: 'Created on March 15, 2024'
 * };
 *
 * const result = rsRecursiveDateFormatter(input);
 * // {
 * //   createdAt: '2024-03-15',
 * //   description: 'Created on March 15, 2024'  // unchanged
 * // }
 * ```
 *
 * @example
 * **With custom format:**
 * ```typescript
 * const input = { timestamp: new Date('2024-03-15T10:30:45Z') };
 *
 * const result = rsRecursiveDateFormatter(input, 'YYYY-MM-DDTHH:mm:ssZ');
 * // { timestamp: '2024-03-15T10:30:45Z' }
 * ```
 *
 * @example
 * **With format map (per-property formatting):**
 * ```typescript
 * const input = {
 *   createdAt: new Date('2024-03-15T10:30:00Z'),
 *   user: {
 *     birthDate: new Date('1990-05-20T00:00:00Z')
 *   }
 * };
 *
 * const formatMap = {
 *   'createdAt': 'YYYY-MM-DDTHH:mm:ssZ',
 *   'user.birthDate': 'DD.MM.YYYY'
 * };
 *
 * const result = rsRecursiveDateFormatter(input, 'YYYY-MM-DD', formatMap);
 * // {
 * //   createdAt: '2024-03-15T10:30:00Z',
 * //   user: {
 * //     birthDate: '20.05.1990'
 * //   }
 * // }
 * ```
 *
 * @example
 * **Arrays are processed recursively:**
 * ```typescript
 * const input = {
 *   dates: [
 *     new Date('2024-03-15T10:30:00Z'),
 *     'March 15, 2024',
 *     '2024-03-16T10:30:00Z'
 *   ]
 * };
 *
 * const result = rsRecursiveDateFormatter(input);
 * // {
 * //   dates: [
 * //     '2024-03-15',
 * //     'March 15, 2024',  // unchanged (not ISO format)
 * //     '2024-03-16'
 * //   ]
 * // }
 * ```
 */
export function rsRecursiveDateFormatter<T extends RsObjectLiteral>(
    objectToFormat: T,
    format: RsDateFormatContext = RS_DEFAULT_DATE_FORMAT_FOR_REQUESTS,
    formatMap: RsDateFormatMapContext = {},
): RsRecursiveFormattedDates<T> {
  const obj = deepClone(objectToFormat);
  const visited = new WeakSet<object>();

  function recurse(value: unknown, currentPath: string, currentFormatMap: RsDateFormatMapContext): unknown {
    // Get format for current property (from formatMap or default)
    const currentFormat = currentFormatMap[currentPath] ?? format;

    if (value instanceof Date) {
      return formatDateLocal(value, currentFormat);
    }

    if (isStrictISODateString(value)) {
      return formatDateLocal(new Date(value), currentFormat);
    }

    if (Array.isArray(value)) {
      // Check for circular reference in arrays
      if (visited.has(value)) {
        console.warn(`[rsRecursiveDateFormatter] Circular reference detected at path: ${currentPath}`);
        return value; // Return as-is to avoid infinite loop
      }
      visited.add(value);

      const result = value.map((item) => recurse(item, currentPath, currentFormatMap));
      visited.delete(value);
      return result;
    }

    if (isPlainObject(value)) {
      // Check for circular reference in objects
      if (visited.has(value)) {
        console.warn(`[rsRecursiveDateFormatter] Circular reference detected at path: ${currentPath}`);
        return value; // Return as-is to avoid infinite loop
      }
      visited.add(value);

      const result: Record<string, unknown> = {};

      for (const key of Object.keys(value)) {
        const nestedPath = currentPath ? `${currentPath}.${key}` : key;

        // Build a nested format map for this path
        const nestedFormatMap: RsDateFormatMapContext = {};
        for (const mapKey of Object.keys(currentFormatMap)) {
          if (mapKey.startsWith(`${nestedPath}.`)) {
            const formatValue = currentFormatMap[mapKey];
            if (formatValue) {
              nestedFormatMap[mapKey] = formatValue;
            }
          }
        }

        result[key] = recurse(
          value[key],
          nestedPath,
          Object.keys(nestedFormatMap).length > 0 ? nestedFormatMap : currentFormatMap,
        );
      }

      visited.delete(value);
      return result;
    }

    return value;
  }

  return recurse(obj, '', formatMap) as RsRecursiveFormattedDates<T>;
}

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * Type utility that transforms Date properties in type T to strings.
 *
 * Used to reflect the type transformation performed by rsRecursiveDateFormatter.
 */
type RsRecursiveFormattedDates<T> = {
  [K in keyof T]: T[K] extends Date ? string : T[K];
};
